import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { US_STATES } from '../../../constants/states';
import NumberFormat from 'react-number-format';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
}));

// Custom phone input component that matches TextField styling
const PhoneTextField = React.forwardRef((props, ref) => {
  return (
    <NumberFormat
      {...props}
      getInputRef={ref}
      customInput={TextField}
      format={(val) => {
        const digits = val.replace(/\D/g, '');
        if (digits.length <= 10) {
          // US format
          return digits.length > 6
            ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
            : digits.length > 3
            ? `${digits.slice(0, 3)}-${digits.slice(3)}`
            : digits.length > 0
            ? `${digits}`
            : '';
        } else {
          // International format
          return `${digits.slice(0, digits.length - 10)} ${digits.slice(
            -10,
            -7
          )} ${digits.slice(-7, -4)}-${digits.slice(-4)}`;
        }
      }}
      allowEmptyFormatting={false}
      mask="_"
    />
  );
});

const AddressContactModal = ({ open, onClose, onSave, currentData = {} }) => {
  const classes = useStyles();
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    businessName: '',
    phone: '',
    labAccountNumber: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  // Initialize form data when modal opens
  React.useEffect(() => {
    if (open) {
      setFormData({
        firstName: currentData.firstName || '',
        lastName: currentData.lastName || '',
        businessName: currentData.businessName || '',
        phone: currentData.phone || '',
        labAccountNumber: currentData.labAccountNumber || '',
        address1: currentData.address1 || '',
        address2: currentData.address2 || '',
        city: currentData.city || '',
        state: currentData.state || '',
        zipCode: currentData.zipCode || '',
      });
      setErrors({});
      setLoading(false);
    }
  }, [open, currentData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (
      !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-\(\)]/g, ''))
    ) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.address1) {
      newErrors.address1 = 'Address is required';
    }
    if (!formData.city) {
      newErrors.city = 'City is required';
    }
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    if (!formData.zipCode) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Please enter a valid ZIP code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trim all form data before validation and submission
    const trimmedFormData = Object.keys(formData).reduce((acc, key) => {
      acc[key] =
        typeof formData[key] === 'string'
          ? formData[key].trim()
          : formData[key];
      return acc;
    }, {});

    // Update form data with trimmed values
    setFormData(trimmedFormData);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const result = await onSave(trimmedFormData);
      if (result.success) {
        onClose();
      } else {
        setErrors({ general: result.message || 'Failed to save information' });
      }
    } catch (error) {
      setErrors({ general: error.message || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      businessName: '',
      phone: '',
      labAccountNumber: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
    });
    setErrors({});
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Address & Contact Information</DialogTitle>
      <DialogContent>
        The address info below is for your shipping address. If you need to ship
        orders direct to a customer or a different address please use the Drop
        Ship options at checkout and keep your address here.
      </DialogContent>
      <form onSubmit={handleSubmit} className={classes.form}>
        <DialogContent>
          {errors.general && (
            <Typography className={classes.errorText} gutterBottom>
              {errors.general}
            </Typography>
          )}

          <Grid container spacing={2}>
            {/* Name Section */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName}
                className={classes.field}
                autoComplete="given-name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName}
                className={classes.field}
                autoComplete="family-name"
              />
            </Grid>

            {/* Business Name */}
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                label="Business Name"
                value={formData.businessName}
                onChange={handleInputChange('businessName')}
                className={classes.field}
                autoComplete="organization"
              />
            </Grid>

            {/* Phone and Lab Account Number */}
            <Grid item xs={12} sm={6}>
              <PhoneTextField
                variant="outlined"
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                error={!!errors.phone}
                helperText={errors.phone}
                className={classes.field}
                placeholder="555-123-4567"
                autoComplete="tel"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="Lab Account Number (Optional)"
                value={formData.labAccountNumber}
                onChange={handleInputChange('labAccountNumber')}
                className={classes.field}
                placeholder="Enter your lab account number"
                autoComplete="off"
              />
            </Grid>

            {/* Address Section */}
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                label="Address Line 1"
                value={formData.address1}
                onChange={handleInputChange('address1')}
                error={!!errors.address1}
                helperText={errors.address1}
                className={classes.field}
                autoComplete="street-address"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                label="Address Line 2 (Optional)"
                value={formData.address2}
                onChange={handleInputChange('address2')}
                className={classes.field}
                placeholder="Apartment, suite, etc."
                autoComplete="address-line2"
              />
            </Grid>

            {/* City, State, ZIP */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="City"
                value={formData.city}
                onChange={handleInputChange('city')}
                error={!!errors.city}
                helperText={errors.city}
                className={classes.field}
                autoComplete="address-level2"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl
                fullWidth
                className={classes.field}
                error={!!errors.state}
                variant="outlined"
              >
                <InputLabel htmlFor="state-select">State</InputLabel>
                <Select
                  id="state-select"
                  value={formData.state}
                  onChange={handleInputChange('state')}
                  label="State"
                  autoComplete="address-level1"
                >
                  <MenuItem value="">
                    <em>Select a state</em>
                  </MenuItem>
                  {US_STATES.map((state) => (
                    <MenuItem key={state.short} value={state.short}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.state && (
                  <Typography className={classes.errorText}>
                    {errors.state}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                variant="outlined"
                fullWidth
                label="ZIP Code"
                value={formData.zipCode}
                onChange={handleInputChange('zipCode')}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                className={classes.field}
                placeholder="12345"
                autoComplete="postal-code"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddressContactModal;
